import {
  getV1CompaniesCompanyIdSiteRateSchedulesSiteRateScheduleIdAutoAddOnAssignments,
  RateOwnerType,
} from '@treadinc/horizon-api-spec';
import { useCallback, useEffect, useRef } from 'react';

import { AddOnAssignment } from '~hooks/useAddOns/models';
import type { Order } from '~hooks/useOrders/models';
import type { Project } from '~hooks/useProjects/models';
import type { RateBasicWithValue } from '~hooks/useRates/models';
import type { SiteRateSchedule } from '~hooks/useSiteRateSchedule/models';
import { useSiteRateSchedule } from '~hooks/useSiteRateSchedule/useSiteRateSchedule';

import type { OrderFormV3FieldValues } from '../OrderFormV3Context';
import { loadAutoAddOnAssignments } from './loadAutoAddOnAssignments';

/**
 * Triggers: the selected `project`'s id (watched, via the Provider),
 *           `pickUpWayPoint.site.id` (watched, via the Provider),
 *           `isCompletedOrder`, and the `defaultOrder` identity inputs. Keying
 *           on the project OBJECT loops — see the dep note in the effect body.
 * Writes:   the Provider's SRS state — `projectHasSiteRates`,
 *           `projectHasSiteRatesLoaded`, `completedOrderRules`,
 *           `completedOrderRulesLoaded`,
 *           `orderSiteRateSchedules`, `projectSiteRates`, the three
 *           `siteRate*AddOnAssignments`, and `isFreightRatesBlockVisible`
 *           (auto-show only — it never hides).
 * Guards:   completed orders skip the per-pickup fetch (their rules arrive
 *           whole and are matched here); a gate skips matching when the
 *           project has no SRS at all; an initial-mount marker preserves
 *           manually-adjusted add-ons on edit-mode orders that already
 *           carry rates.
 * Legacy:   the legacy form's fetchSiteRates effect, completed-order match,
 *           auto-visibility, and SRS-presence resolution. The
 *           `setUseVendorRateForDriver(false)` calls live in
 *           effects/useVendorRateToDriverCopy.ts, reacting to the
 *           `projectSiteRates` writes made here.
 * Test:     __tests__/OrderFormV3Context.test.tsx ("site-rate-schedule sync").
 */

/** Fetches per-owner add-on assignments for a matched SRS rule. */
async function fetchSiteRateAddOnAssignments(
  formCompanyId: string,
  siteRateId: string,
  rateOwnerType: RateOwnerType,
): Promise<AddOnAssignment[]> {
  const response =
    await getV1CompaniesCompanyIdSiteRateSchedulesSiteRateScheduleIdAutoAddOnAssignments({
      path: { 'company-id': formCompanyId, 'site-rate-schedule-id': siteRateId },
      query: {
        'filter[account_type]':
          rateOwnerType !== RateOwnerType.DRIVER ? rateOwnerType : undefined,
      },
    });

  return response.data.data.map((addOnAssignment) =>
    AddOnAssignment.parse(addOnAssignment),
  );
}

// Order schedule reads use the dispatch-as company (orders are visible to participants).
// Add-ons use the form's company because a schedule can belong to a project owned by
// another company.
export function useSiteRateScheduleSync({
  dispatchAsCompanyId,
  formCompanyId,
  watchedProject,
  watchedPickUpWaypointSiteId,
  defaultOrder,
  isCopyAndEdit,
  isCompletedOrder,
  projectData,
  primaryCustomerRate,
  projectSiteRates,
  projectHasSiteRates,
  completedOrderRules,
  setProjectHasSiteRates,
  setProjectSiteRates,
  setCompletedOrderRules,
  setCompletedOrderRulesLoaded,
  setProjectHasSiteRatesLoaded,
  setOrderSiteRateSchedules,
  setSiteRateCustomerAddOnAssignments,
  setSiteRateVendorAddOnAssignments,
  setSiteRateDriverAddOnAssignments,
  setIsFreightRatesBlockVisible,
}: {
  dispatchAsCompanyId: string;
  formCompanyId: string;
  watchedProject: OrderFormV3FieldValues['project'] | undefined;
  watchedPickUpWaypointSiteId: string | undefined;
  defaultOrder: Order | null;
  isCopyAndEdit: boolean;
  isCompletedOrder: boolean;
  projectData: Project | null;
  primaryCustomerRate: RateBasicWithValue | null;
  projectSiteRates: SiteRateSchedule | undefined;
  projectHasSiteRates: boolean;
  completedOrderRules: SiteRateSchedule[];
  setProjectHasSiteRates: (has: boolean) => void;
  setProjectSiteRates: (rates: SiteRateSchedule | undefined) => void;
  setCompletedOrderRules: (rules: SiteRateSchedule[]) => void;
  setCompletedOrderRulesLoaded: (loaded: boolean) => void;
  setProjectHasSiteRatesLoaded: (loaded: boolean) => void;
  setOrderSiteRateSchedules: (rules: SiteRateSchedule[]) => void;
  setSiteRateCustomerAddOnAssignments: (a: AddOnAssignment[] | undefined) => void;
  setSiteRateVendorAddOnAssignments: (a: AddOnAssignment[] | undefined) => void;
  setSiteRateDriverAddOnAssignments: (a: AddOnAssignment[] | undefined) => void;
  setIsFreightRatesBlockVisible: (visible: boolean) => void;
}): void {
  const {
    getAllOrderSiteRateSchedules,
    getAllProjectSiteRateSchedules,
    getProjectSiteRateSchedules,
  } = useSiteRateSchedule();

  const clearSiteRates = useCallback(() => {
    setProjectSiteRates(undefined);
    setCompletedOrderRules([]);
    setSiteRateCustomerAddOnAssignments(undefined);
    setSiteRateVendorAddOnAssignments(undefined);
    setSiteRateDriverAddOnAssignments(undefined);
  }, [
    setProjectSiteRates,
    setCompletedOrderRules,
    setSiteRateCustomerAddOnAssignments,
    setSiteRateVendorAddOnAssignments,
    setSiteRateDriverAddOnAssignments,
  ]);

  // Skip site-rate add-on fetching on the first run (initial mount) in edit
  // mode so manually-adjusted add-ons on the order are preserved; subsequent
  // runs (the user changed the pickup site) DO fetch so the downstream
  // reconciliation can clean up stale inherited entries (TRE-13962).
  const isInitialSiteRateFetch = useRef(true);

  // ——— SRS-presence resolution ———
  // Copy & Edit creates a new order, so the new record should reference the
  // project's rate book whenever possible, falling back to the source
  // order's own stamped SRS only when the project has none. Regular Edit
  // keeps order-first precedence so writes land on the order, not silently
  // on the project's shared records.
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isCompletedOrder && defaultOrder?.id) {
          try {
            const rules = await getAllOrderSiteRateSchedules(
              dispatchAsCompanyId,
              defaultOrder.id,
            );
            setProjectHasSiteRates(rules.length > 0);
            setCompletedOrderRules(rules);
            setOrderSiteRateSchedules([]);
          } finally {
            // TRE-16198: mark the order-scoped lookup resolved even when it
            // throws. The rates panel withholds `projectId` until this flips,
            // so leaving it false on failure would strand FreightRates with no
            // source at all — the very blank section this ticket fixed.
            setCompletedOrderRulesLoaded(true);
          }
        } else if (defaultOrder?.id) {
          let resolved = false;
          if (isCopyAndEdit && watchedProject?.id) {
            const projectRules = await getAllProjectSiteRateSchedules(watchedProject.id);
            if (projectRules.length > 0) {
              setOrderSiteRateSchedules([]);
              setProjectHasSiteRates(true);
              resolved = true;
            }
          }
          if (!resolved) {
            const orderRules = await getAllOrderSiteRateSchedules(
              dispatchAsCompanyId,
              defaultOrder.id,
            );
            if (orderRules.length > 0) {
              setOrderSiteRateSchedules(orderRules);
              setProjectHasSiteRates(true);
            } else {
              setOrderSiteRateSchedules([]);
              if (watchedProject?.id) {
                const projectRules = await getAllProjectSiteRateSchedules(
                  watchedProject.id,
                );
                setProjectHasSiteRates(projectRules.length > 0);
              } else {
                setProjectHasSiteRates(false);
              }
            }
          }
        } else if (watchedProject?.id) {
          const rules = await getAllProjectSiteRateSchedules(watchedProject.id);
          setProjectHasSiteRates(rules.length > 0);
          setOrderSiteRateSchedules([]);
        } else {
          setProjectHasSiteRates(false);
          setOrderSiteRateSchedules([]);
        }
      } catch (error) {
        console.error('Failed to fetch site rate schedules:', error);
      } finally {
        // Resolved even on failure: consumers read a false `projectHasSiteRates` as "the project owns no
        // cards", so leaving this false would strand them on that reading forever.
        setProjectHasSiteRatesLoaded(true);
      }
    };

    // Back to unresolved for this (project, order) pair before refetching — `projectHasSiteRates` still
    // holds the PREVIOUS pair's answer until the fetch lands, and reading it as settled would apply the
    // old project's card presence to the new one.
    setProjectHasSiteRatesLoaded(false);
    void fetchData();
    // Key on the project ID, NOT the watched project object (TRE-15128): the
    // Provider's useWatch('project') returns a fresh object reference on every
    // render, so an object dep refetches every render — an infinite SRS request
    // loop on the edit form. The id is what selects the project's rate book;
    // the mode bits and order identity round out the deps. Fetchers are read
    // via closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedProject?.id,
    isCompletedOrder,
    isCopyAndEdit,
    defaultOrder?.id,
    dispatchAsCompanyId,
  ]);

  // ——— Per-(project, pickup) rule fetch + add-on cascade ———
  useEffect(() => {
    const fetchSiteRates = async () => {
      if (isCompletedOrder) {
        // Completed orders fetch all SRS in the presence effect above and
        // derive the matching rate below — no separate call needed.
        return;
      }

      if (watchedProject?.id && watchedPickUpWaypointSiteId) {
        // TRE-14681: skip matching and the add-on reconcile entirely when the
        // project doesn't use site rates — without this gate, pickup-site
        // changes on a non-SRS project would fire the `[]` reconcile that
        // wipes the project-level add-on cascade. Re-checking inline (instead
        // of reading `projectHasSiteRates` state) avoids racing the presence
        // effect above.
        const projectRules = await getAllProjectSiteRateSchedules(watchedProject.id);
        if (projectRules.length === 0) {
          clearSiteRates();
          isInitialSiteRateFetch.current = false;
          return;
        }

        const rates = await getProjectSiteRateSchedules({
          projectId: watchedProject.id,
          pickupSiteId: watchedPickUpWaypointSiteId,
        });
        if (rates?.[0]) {
          setProjectSiteRates(rates[0]);

          // Fetch site-rate add-on assignments when it's a new order, when
          // the user changed the pickup site (TRE-13962), or on the initial
          // edit-mode mount of an order with no rates of its own (TRE-14133).
          // Skip ONLY the initial edit-mode mount of an order that already
          // has rates, to avoid clobbering add-ons tied to those rates.
          const hasDefinedRate = (owner: 'customer' | 'vendor' | 'driver') => {
            if (!defaultOrder) {
              return false;
            }
            const rate =
              owner === 'customer'
                ? defaultOrder.customerRate
                : owner === 'vendor'
                  ? defaultOrder.vendorRate
                  : defaultOrder.driverRate;
            const rateType =
              owner === 'customer'
                ? defaultOrder.customerRateType
                : owner === 'vendor'
                  ? defaultOrder.vendorRateType
                  : defaultOrder.driverRateType;
            const value =
              owner === 'customer'
                ? defaultOrder.customerRateValue
                : owner === 'vendor'
                  ? defaultOrder.vendorRateValue
                  : defaultOrder.driverRateValue;
            return Boolean(rate || rateType || (value !== null && value !== undefined));
          };
          const orderHasAnyRate =
            hasDefinedRate('customer') ||
            hasDefinedRate('vendor') ||
            hasDefinedRate('driver');
          const isInitialEditMountWithRate =
            Boolean(defaultOrder?.id) &&
            isInitialSiteRateFetch.current &&
            orderHasAnyRate;

          if (!isInitialEditMountWithRate && rates[0].id) {
            const siteRateId = rates[0].id;
            const setters = [
              [RateOwnerType.CUSTOMER, setSiteRateCustomerAddOnAssignments],
              [RateOwnerType.VENDOR, setSiteRateVendorAddOnAssignments],
              [RateOwnerType.DRIVER, setSiteRateDriverAddOnAssignments],
            ] as const;
            for (const [rateOwnerType, apply] of setters) {
              void loadAutoAddOnAssignments(
                () =>
                  fetchSiteRateAddOnAssignments(formCompanyId, siteRateId, rateOwnerType),
                apply,
              );
            }
          }
        } else {
          // No site rates for this project/site pair. When editing and the
          // user changed the pickup to a site with no freight rule, set the
          // add-on state to [] (not undefined) so the override triggers
          // reconciliation cleanup of stale inherited entries (TRE-13962).
          clearSiteRates();
          if (defaultOrder?.id && !isInitialSiteRateFetch.current) {
            setSiteRateCustomerAddOnAssignments([]);
            setSiteRateVendorAddOnAssignments([]);
            setSiteRateDriverAddOnAssignments([]);
          }
        }
        // Only commit "initial fetch complete" once a real (project, pickup)
        // pair was processed — flipping unconditionally would consume the
        // marker on a premature run and make the real first run look like a
        // user-driven change.
        isInitialSiteRateFetch.current = false;
      } else {
        // No project or pickup site selected — clear site rates.
        clearSiteRates();
      }
    };

    void fetchSiteRates();
    // Everything else is read via closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatchAsCompanyId,
    formCompanyId,
    watchedProject?.id,
    watchedPickUpWaypointSiteId,
    isCompletedOrder,
  ]);

  // ——— Completed orders match a rule per pickup site ———
  // Derived from the fetched rules whenever the pickup changes, to avoid the
  // stale closure a fetch-callback match would create.
  useEffect(() => {
    if (!isCompletedOrder || completedOrderRules.length === 0) {
      return;
    }

    // A rule without a pickup-site condition is a base card that applies to
    // every pickup site, so it matches when no site-specific rule does. The
    // order-scoped snapshots the BE stamps on dereference carry the project
    // card's conditions verbatim — a base card arrives with no pickup site —
    // and an exact-id-only match would skip it entirely.
    const matched = watchedPickUpWaypointSiteId
      ? (completedOrderRules.find(
          (rule) => rule.pickupSite?.id === watchedPickUpWaypointSiteId,
        ) ?? completedOrderRules.find((rule) => !rule.pickupSite))
      : completedOrderRules[0];

    if (matched) {
      setProjectSiteRates(matched);
    } else {
      // Clear only the matched-rule state. `clearSiteRates()` here would also
      // wipe `completedOrderRules`, un-suppressing `projectId` in the rates
      // panel and letting FreightRates fall back to the project's LIVE rate
      // book — the completed order would display the project's current rates
      // instead of its own frozen snapshot.
      setProjectSiteRates(undefined);
    }
    // Legacy deps verbatim — isCompletedOrder is mount-derived and read via
    // closure like legacy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedOrderRules, watchedPickUpWaypointSiteId]);

  // ——— Auto-show the freight-rates block ———
  // Any rate value on the order/project/SRS rule, a primary customer rate,
  // or SRS presence exposes the block. Show-only: hiding stays manual.
  useEffect(() => {
    if (
      defaultOrder?.customerRateValue ||
      defaultOrder?.vendorRateValue ||
      defaultOrder?.driverRateValue ||
      projectData?.customerRateValue ||
      projectData?.vendorRateValue ||
      projectData?.driverRateValue ||
      projectSiteRates?.customerRateValue ||
      projectSiteRates?.vendorRateValue ||
      projectSiteRates?.driverRateValue ||
      primaryCustomerRate ||
      projectHasSiteRates
    ) {
      setIsFreightRatesBlockVisible(true);
    }
    // Legacy deps verbatim.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    defaultOrder,
    projectData,
    projectSiteRates,
    primaryCustomerRate,
    projectHasSiteRates,
  ]);
}
